import React, { memo } from 'react';
import Image from 'next/image';
import getIndianCurrencyFormat from '@helpers/getIndianCurrencyFormat';
import getMonthlyEMI from '@helpers/getMonthlyEMI';
import assetLoader from '@utils/internalImageLoder';
import AckoDriveLogo from '../../atoms/AckoDriveLogo';
import styles from './CarFinance.module.scss';
import type { SavingsBreakupData, BankData } from '@interfaces/CarFinance.types';

interface SavingsTableProps {
  isLoggedIn: boolean;
  onRoadPrice: number;
  savingsBreakupData: SavingsBreakupData;
  ADPrice: number;
  carLoanDiscount: number;
  downPayment: number;
  otherInterest: number | string;
  loanTenure: number;
  selectedBank: BankData | null;
  initialSelectedBank: BankData | null;
  getRIPPlusConsumer: (savingsBreakup: any) => number;
  getTotalEmiSavings: () => string;
  totalAckoDriveSavings: string;
  handleEditClick: (mode: "downpayment" | "tenure" | "interest") => void;
}

export const SavingsTable = memo(({
  isLoggedIn,
  onRoadPrice,
  savingsBreakupData,
  ADPrice,
  carLoanDiscount,
  downPayment,
  otherInterest,
  loanTenure,
  selectedBank,
  initialSelectedBank,
  getRIPPlusConsumer,
  getTotalEmiSavings,
  totalAckoDriveSavings,
  handleEditClick,
}: SavingsTableProps) => {
  return (
    <div className={styles.savingsTableCard}>
      <div className={styles.savingsBanner}>
        <Image
          src={`${assetLoader()}/icons/discount_icon.svg`}
          alt="discount"
          width={24}
          height={24}
          loading="lazy"
        />
        <span className={styles.savingsBannerText}>
          You&apos;ll save{" "}
          <span className={styles.savingsAmount}>
            {`₹${totalAckoDriveSavings}`}
          </span>{" "}
          with ACKO Drive
        </span>
      </div>
      <table className={styles.emiTable}>
        <tr className={styles.emiTableR}>
          <th className={styles.emiTableEmptyTH} />
          <th className={styles.emiTableTHDealer}>Other Dealer</th>
          <th className={`${styles.emiTableTH} ${styles.logoTh}`}>
            <AckoDriveLogo className={styles.logoWrapper} />
          </th>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={styles.emiTableText}>On road Price</td>
          <td className={styles.emiTableDealerData}>
            {`₹${getIndianCurrencyFormat(
              isLoggedIn
                ? Number(onRoadPrice) - getRIPPlusConsumer(savingsBreakupData)
                : onRoadPrice
            )}`}
          </td>
          <td className={styles.emiTableADData}>
            {`₹${getIndianCurrencyFormat(
              isLoggedIn
                ? Number(onRoadPrice) - getRIPPlusConsumer(savingsBreakupData)
                : onRoadPrice
            )}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={styles.emiTableText}>Discount from ACKO Drive</td>
          <td className={styles.emiTableDealerData}>-</td>
          <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
            {`₹${getIndianCurrencyFormat(
              savingsBreakupData?.ackodiscount + Number(savingsBreakupData?.newyear_discount || 0)
            )}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={styles.emiTableText}>Car loan discount</td>
          <td className={styles.emiTableDealerData}>-</td>
          <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
            {`₹${getIndianCurrencyFormat(carLoanDiscount)}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={`${styles.emiTableText} ${styles.isBold} ${styles.hasBorder}`}>
            Car Price
          </td>
          <td className={`${styles.emiTableDealerData} ${styles.isBold} ${styles.hasBorder}`}>
            {`₹${getIndianCurrencyFormat(
              Number(onRoadPrice || 0) - getRIPPlusConsumer(savingsBreakupData)
            )}`}
          </td>
          <td className={`${styles.emiTableADData} ${styles.isBold} ${styles.hasBorder}`}>
            {`₹${getIndianCurrencyFormat(ADPrice)}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td
            className={`${styles.emiTableText} ${styles.isEditable}`}
            onClick={() => handleEditClick("downpayment")}
          >
            Down Payment
            <Image
              src={`${assetLoader()}/icons/Edit_Pencil.svg`}
              alt="Edit"
              width={16}
              height={16}
            />
          </td>
          <td className={styles.emiTableDealerData}>
            {`₹${getIndianCurrencyFormat(downPayment)}`}
          </td>
          <td className={`${styles.emiTableADData}`}>
            {`₹${getIndianCurrencyFormat(downPayment)}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={styles.emiTableText}>Loan Amount</td>
          <td className={styles.emiTableDealerData}>
            {`₹${getIndianCurrencyFormat(
              Number(onRoadPrice) - getRIPPlusConsumer(savingsBreakupData) - Number(downPayment)
            )}`}
          </td>
          <td className={styles.emiTableADData}>
            {`₹${getIndianCurrencyFormat(Number(ADPrice) - Number(downPayment))}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td
            className={`${styles.emiTableText} ${styles.isEditable}`}
            onClick={() => handleEditClick("interest")}
          >
            Interest
            <Image
              src={`${assetLoader()}/icons/Edit_Pencil.svg`}
              alt="Edit"
              width={16}
              height={16}
            />
          </td>
          <td className={`${styles.emiTableDealerData} ${styles.isSemiBold}`}>
            {otherInterest ? Number(otherInterest).toFixed(2) + "%" : "-"}
          </td>
          <td className={`${styles.emiInterestTableADData} ${styles.isSemiBold}`}>
            {`${selectedBank?.interest_rate}%`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td
            className={`${styles.emiTableText} ${styles.isEditable}`}
            onClick={() => handleEditClick("tenure")}
          >
            Duration
            <Image
              src={`${assetLoader()}/icons/Edit_Pencil.svg`}
              alt="Edit"
              width={16}
              height={16}
            />
          </td>
          <td className={styles.emiTableDealerData}>
            {`${loanTenure} years`}
          </td>
          <td className={`${styles.emiTableADData}`}>
            {`${loanTenure} years`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={`${styles.emiTableText} ${styles.isBold} ${styles.hasBorder}`}>
            Monthly EMI
          </td>
          <td className={`${styles.emiTableDealerData} ${styles.isBold} ${styles.hasBorder}`}>
            {`₹${getMonthlyEMI(
              Number(onRoadPrice) - getRIPPlusConsumer(savingsBreakupData) - Number(downPayment),
              Number(otherInterest),
              Number(loanTenure)
            )}`}
          </td>
          <td className={`${styles.emiTableADData} ${styles.isBold} ${styles.hasBorder}`}>
            {`₹${getMonthlyEMI(
              Number(ADPrice) - Number(downPayment),
              Number(initialSelectedBank?.interest_rate),
              Number(loanTenure)
            )}`}
          </td>
        </tr>
        <tr className={styles.emiTableR}>
          <td className={styles.emiTableText}>
            {`Total EMI Savings (${loanTenure} years)`}
          </td>
          <td className={styles.emiTableDealerData}>-</td>
          <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
            {getTotalEmiSavings()}
          </td>
        </tr>
      </table>
    </div>
  );
});

SavingsTable.displayName = 'SavingsTable';
